(function () {
  // ---------- CONFIG ----------
  const SF_ENDPOINT = () =>
    `https://${window.__shopDomain}/api/${window.__sfApiVersion}/graphql.json`;

  // ---------- GRAPHQL ----------
  async function gql(query, variables) {
    if (!window.__sfToken) throw new Error('Missing Storefront token');
    const res = await fetch(SF_ENDPOINT(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.__sfToken
      },
      body: JSON.stringify({ query, variables })
    });
    const json = await res.json();
    if (!res.ok || json.errors) {
      const msg = (json.errors && json.errors[0] && json.errors[0].message) || res.statusText;
      throw new Error(msg || 'Storefront API error');
    }
    return json.data;
  }

  // variant_ids[] -> map { [numericId]: { etaDays, forventet, locInfo, locValid } }
  async function fetchEtaForVariants(variantIds) {
    const unique = Array.from(new Set(variantIds.map(String)));
    if (!unique.length) return {};

    // Build GraphQL IDs and a reverse lookup for BOTH base64 and plain forms
    const idMap = {};
    const gids = unique.map(n => {
      const plain = `gid://shopify/ProductVariant/${n}`;
      const b64 = btoa(plain);
      idMap[plain] = n;
      idMap[b64] = n;
      return b64;
    });

    const query = `
      query EtaForVariants($ids: [ID!]!, $etaNs: String!, $etaKey: String!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            product { tags }
            eta: metafield(namespace: $etaNs, key: $etaKey) { value }
            loc: metafield(namespace: "geekd", key: "locInfo") { value }
          }
        }
      }
    `;

    const data = await gql(query, {
      ids: gids,
      etaNs: window.__etaNamespace,
      etaKey: window.__etaKey
    });

    const out = {};
    (data.nodes || []).forEach(node => {
      if (!node || !node.id) return;

      const numericId = idMap[node.id];
      if (!numericId) return;

      // ETA days
      let etaDays = Number(node.eta?.value);
      if (!Number.isFinite(etaDays) || etaDays <= 0) etaDays = null;

      // Fallback ETA from tag __leveringstid:N
      if (!etaDays && Array.isArray(node.product?.tags)) {
        const t = node.product.tags.find(x => x.startsWith('__leveringstid:'));
        if (t) {
          const n = Number(t.split('__leveringstid:').pop());
          if (Number.isFinite(n) && n > 0) etaDays = n;
        }
      }

      // Parse locInfo JSON
      let locInfo = null;
      let locValid = false;
      if (node.loc?.value) {
        try {
          const parsed = JSON.parse(node.loc.value);
          const k = Number(parsed?.Kolding);
          const f = Number(parsed?.Fjernlager);
          if (Number.isFinite(k)) {
            locInfo = { Kolding: k, Fjernlager: Number.isFinite(f) ? f : null };
            locValid = true;
          }
        } catch (_) { /* invalid JSON -> locValid stays false */ }
      }

      out[String(numericId)] = {
        etaDays,
        forventet: etaDays ? `${etaDays - 1} - ${etaDays} hverdage` : '',
        locInfo,
        locValid
      };
    });

    return out;
  }

  function shouldApplyEta(meta, qtyForVariant) {
    // If locInfo missing/invalid -> apply ETA
    if (!meta?.locValid) return true;

    // If no ETA number, nothing to apply
    if (!Number.isFinite(meta?.etaDays) || meta.etaDays <= 0) return false;

    const kolding = Number(meta.locInfo?.Kolding);
    if (!Number.isFinite(kolding)) return true; // treat as invalid → apply

    // Apply ETA only when Kolding stock is LESS than total quantity in cart for this variant
    return kolding < qtyForVariant;
  }

  // ---------- CART PATCH ----------
  async function ensureEtaInCart() {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());

      // Build total quantity per variant across ALL lines
      const qtyByVariant = {};
      for (const it of cart.items) {
        const v = String(it.variant_id);
        qtyByVariant[v] = (qtyByVariant[v] || 0) + (it.quantity || 0);
      }

      const missing = cart.items.filter(it => {
        const p = it.properties || {};
        return !(p.EtaDays || p['Forventet leveringstid']);
      });
      if (!missing.length) return;

      const variantIds = Array.from(new Set(missing.map(m => m.variant_id)));
      const map = await fetchEtaForVariants(variantIds);

      const ops = [];
      for (const it of missing) {
        const vIdStr = String(it.variant_id);
        const meta = map[vIdStr];

        const qtyForVariant = qtyByVariant[vIdStr] || it.quantity || 1;
        const apply = shouldApplyEta(meta, qtyForVariant);
        if (!apply) continue;
        if (!Number.isFinite(meta?.etaDays) || meta.etaDays <= 0) continue;

        ops.push(fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: it.key,
            properties: {
              ...(it.properties || {}),
              EtaDays: meta.etaDays,
              'Forventet leveringstid': meta.forventet
            }
          })
        }));
      }

      if (ops.length) {
        await Promise.all(ops);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (_) { /* swallow */ }
  }

  // ---------- SUBMIT HOOK ----------
  async function onUniversalSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = (form.getAttribute('action') || '').includes('/cart/add');
    if (!action) return;
    if (form.__etaInjected) return;

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;
    const vId = String(idInput.value || '');

    // Intended quantity from form (default 1)
    const qInput = form.querySelector('input[name="quantity"]');
    const addQty = Number(qInput?.value || 1);

    // Current cart qty for this variant (to compare against Kolding)
    let existingQty = 0;
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      existingQty = (cart.items || [])
        .filter(i => String(i.variant_id) === vId)
        .reduce((s, i) => s + (i.quantity || 0), 0);
    } catch (_) {}

    const totalQty = existingQty + addQty;

    const map = await fetchEtaForVariants([vId]);
    const meta = map[vId];

    if (!shouldApplyEta(meta, totalQty)) return;
    if (!Number.isFinite(meta?.etaDays) || meta.etaDays <= 0) return;

    const i1 = document.createElement('input');
    i1.type = 'hidden';
    i1.name = 'properties[EtaDays]';
    i1.value = meta.etaDays;

    const i2 = document.createElement('input');
    i2.type = 'hidden';
    i2.name = 'properties[Forventet leveringstid]';
    i2.value = meta.forventet;

    form.appendChild(i1);
    form.appendChild(i2);
    form.__etaInjected = true;
  }

  // ---------- NETWORK TAPS ----------
  function installNetworkTaps() {
    // wrap fetch
    const _fetch = window.fetch;
    window.fetch = async function(input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isCartAdd = url.includes('/cart/add');
      const isCartUpdate = url.includes('/cart/update') || url.includes('/cart/change');

      const res = await _fetch.apply(this, arguments);

      try {
        if (isCartAdd && res.ok) {
          let payload = null;
          try { payload = await res.clone().json(); } catch (_) {}

          if (payload && (payload.key || payload.id || payload.variant_id)) {
            const vId = String(payload.variant_id || payload.id || '');
            const map = vId ? await fetchEtaForVariants([vId]) : {};
            const meta = vId ? map[vId] : null;

            // total qty AFTER the add
            let totalQty = 0;
            try {
              const cartNow = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
              totalQty = (cartNow.items || [])
                .filter(i => String(i.variant_id) === vId)
                .reduce((s, i) => s + (i.quantity || 0), 0);
            } catch (_) {}

            if (meta && shouldApplyEta(meta, totalQty)) {
              if (payload.key) {
                await fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'same-origin',
                  body: JSON.stringify({
                    id: payload.key,
                    properties: {
                      ...(payload.properties || {}),
                      EtaDays: meta.etaDays,
                      'Forventet leveringstid': meta.forventet
                    }
                  })
                });
                document.dispatchEvent(new CustomEvent('cart:refresh'));
              } else {
                await ensureEtaInCart();
              }
            }
          } else {
            await ensureEtaInCart();
          }
        } else if (isCartUpdate && res.ok) {
          await ensureEtaInCart();
        }
      } catch (_) { /* swallow */ }

      return res;
    };

    // wrap XHR for older themes
    const _open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      this.addEventListener('load', function() {
        try {
          const u = String(url || '');
          if ((u.includes('/cart/add') || u.includes('/cart/update') || u.includes('/cart/change')) &&
              this.status >= 200 && this.status < 300) {
            ensureEtaInCart();
          }
        } catch (_) {}
      });
      return _open.apply(this, arguments);
    };
  }

  // ---------- READY / EVENTS ----------
  document.addEventListener('submit', onUniversalSubmit, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureEtaInCart);
  } else {
    ensureEtaInCart();
  }

  document.addEventListener('cart:updated', ensureEtaInCart);
  document.addEventListener('theme:cart:updated', ensureEtaInCart);

  installNetworkTaps();
})();
